import { Building2, Workflow, Recycle } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useT } from "@/i18n/LanguageProvider";

function Step({ num, title, body, Icon, image, index, chapterLabel }) {
    const ref = useReveal();
    const flip = index % 2 === 1;
    return (
        <div
            ref={ref}
            data-testid={`step-${index}`}
            className="reveal grid md:grid-cols-12 gap-8 md:gap-12 items-center py-16 md:py-24 border-t border-primary/10"
        >
            <div className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
                <div className="relative overflow-hidden rounded-sm aspect-[4/3] bg-secondary">
                    <img
                        src={image}
                        alt={`${title} — ${body.slice(0, 80)}…`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms] ease-out"
                    />
                    <div className="absolute top-4 left-4 bg-background/85 backdrop-blur px-3 py-1 text-xs tracking-[0.25em] uppercase text-primary">
                        {chapterLabel} {num}
                    </div>
                </div>
            </div>
            <div className={`md:col-span-6 ${flip ? "md:order-1 md:pr-12" : "md:pl-6"}`}>
                <div className="flex items-baseline gap-4 mb-4">
                    <span aria-hidden="true" className="font-display italic text-7xl md:text-8xl text-primary/25 leading-none">
                        {num}
                    </span>
                    <Icon size={28} className="text-accent" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <h3 className="font-display text-5xl md:text-6xl text-primary tracking-tight mb-5">
                    {title}.
                </h3>
                <p className="text-lg leading-relaxed text-foreground/80 max-w-lg">{body}</p>
            </div>
        </div>
    );
}

const IMAGES = [
    "https://images.pexels.com/photos/5099276/pexels-photo-5099276.jpeg",
    "https://images.pexels.com/photos/1555199/pexels-photo-1555199.jpeg",
    "https://images.unsplash.com/photo-1781243680823-aae6c7f1ff12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxyZWN5Y2xpbmclMjBmYWNpbGl0eSUyMHdhcm18ZW58MHx8fHwxNzgyNDg0MTMxfDA&ixlib=rb-4.1.0&q=85",
];

export default function HowItWorks() {
    const { t } = useT();
    const steps = [
        { Icon: Building2, num: "01", title: t("how.step1.title"), body: t("how.step1.body") },
        { Icon: Workflow, num: "02", title: t("how.step2.title"), body: t("how.step2.body") },
        { Icon: Recycle, num: "03", title: t("how.step3.title"), body: t("how.step3.body") },
    ];

    return (
        <section
            id="how"
            data-testid="how-section"
            aria-labelledby="how-headline"
            className="relative py-24 md:py-32 bg-background"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-center gap-6 mb-12">
                    <span aria-hidden="true" className="font-display italic text-6xl md:text-8xl text-primary/30 leading-none">
                        02
                    </span>
                    <div className="text-xs uppercase tracking-[0.3em] text-accent font-semibold">
                        {t("how.chapter")}
                    </div>
                </div>

                <h2
                    id="how-headline"
                    className="font-display text-5xl md:text-7xl text-primary tracking-tight leading-[0.95] max-w-4xl mb-6"
                >
                    {t("how.headline.prefix")} <em className="text-accent">{t("how.headline.accent")}</em> {t("how.headline.suffix")}
                </h2>
                <p className="text-lg text-foreground/70 max-w-xl mb-8">{t("how.intro")}</p>

                <div>
                    {steps.map((s, i) => (
                        <Step
                            key={s.num}
                            index={i}
                            num={s.num}
                            title={s.title}
                            body={s.body}
                            Icon={s.Icon}
                            image={IMAGES[i]}
                            chapterLabel={t("how.chapterLabel")}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
